import { ColumnDef } from '@tanstack/react-table';
import React, { useMemo } from 'react';
import { LuBuilding } from 'react-icons/lu';

import { ImageWithFallbackIcon } from '@/components/ImageWithFallbackIcon';
import { ClientDataTable } from '@/components/table/DataTable';
import { formatCurrencyHuman, formatPercent } from 'utils';

export interface AffectedStock {
    shareClassId: string;
    shareId: string;
    ticker: string;
    name: string;
    marketValue: number;
    percentage: number;
    restricted: boolean;
    logoUrl: string;
}

export interface AffectedStocksTableProps {
    stocks: AffectedStock[];
}

export const AffectedStocksTable = ({ stocks }: AffectedStocksTableProps) => {
    const columns: ColumnDef<AffectedStock>[] = useMemo(
        () => [
            {
                accessorKey: 'ticker',
                header: 'Company',
                enableSorting: true,
                cell: ({ row }) => (
                    <div className="flex items-center gap-2">
                        <ImageWithFallbackIcon
                            src={row.original.logoUrl}
                            alt={`${row.original.name} logo`}
                            fallbackIcon={LuBuilding}
                            className="h-6 w-6 rounded object-cover"
                        />
                        <span className="font-medium">{row.original.ticker}</span>
                    </div>
                ),
            },
            {
                accessorKey: 'name',
                header: 'Name',
                enableSorting: true,
            },
            {
                accessorKey: 'marketValue',
                header: 'Market Cap',
                enableSorting: true,
                cell: ({ row }) => formatCurrencyHuman(row.original.marketValue, 'USD'),
            },
            {
                accessorKey: 'percentage',
                header: 'Percentage',
                enableSorting: true,
                cell: ({ row }) => formatPercent(row.original.percentage),
            },
            {
                accessorKey: 'restricted',
                header: 'Restricted',
                enableSorting: true,
                cell: ({ row }) => (
                    <div className="flex items-center space-x-2">
                        <span
                            className={`text-xs font-medium ${
                                row.original.restricted ? 'text-red-600' : 'text-green-600'
                            }`}
                        >
                            {row.original.restricted ? 'Restricted' : 'Allowed'}
                        </span>
                    </div>
                ),
            },
        ],
        [],
    );

    return (
        <ClientDataTable
            data={stocks}
            columns={columns}
            emptyMessage="No affected stocks found"
            initialState={{ sorting: [{ id: 'marketValue', desc: true }] }}
            className="rounded border bg-white"
        />
    );
};
